import React, { useEffect, useRef, useState, useCallback } from "react";
import { createBeaconsScene, BEACONS_DEFAULTS } from "./beaconsScene";

/**
 * Persistent Beacons Lab.
 *
 * Route: /lab/beacons
 *
 * Modes:
 *   ?v=a|b|c   → debug: renders the named variant with a visible badge.
 *   ?blind=1   → blind trial: randomly assigns A/B/C, hides identity,
 *                asks three neutral questions after playback, reveals
 *                the identity only when the observer requests it.
 *                Replay repeats the SAME variant; "Next trial" is the
 *                only path to a new random assignment.
 *
 * The scene is not touched by mode selection. Materials, lighting,
 * timing, and camera arc are identical across all variants.
 *
 * See /app/memory/BEACONS_BRIEF.md and /app/memory/DESIGN_LANGUAGE.md.
 */

const VARIANTS = ["A", "B", "C"];

const readModeFromUrl = () => {
  const url = new URL(window.location.href);
  const blind = url.searchParams.get("blind") === "1";
  const debrief = url.searchParams.get("debrief") === "1";
  const raw = (url.searchParams.get("v") || "").toLowerCase();
  const vExplicit = raw === "a" ? "A" : raw === "b" ? "B" : raw === "c" ? "C" : null;
  const shownRaw = (url.searchParams.get("shown") || "").toLowerCase();
  const shownVariant =
    shownRaw === "a" ? "A" : shownRaw === "b" ? "B" : shownRaw === "c" ? "C" : null;
  return { blind, debrief, vExplicit, shownVariant };
};

const pickRandom = () => VARIANTS[Math.floor(Math.random() * VARIANTS.length)];

const VARIANT_LABELS = {
  A: "A · smoother",
  B: "B · micro-roughness",
  C: "C · shallow latent planes",
};

export default function BeaconsLab() {
  const { debrief } = readModeFromUrl();
  // Debrief mode is a completely separate surface: no scene, no canvas,
  // no facilitator instruments. It exists to gather answers from
  // attendees after they've experienced the keynote. Rendered as its own
  // component so the lab component isn't burdened with a second lifecycle.
  if (debrief) return <DebriefSurface />;
  return <ExperimentalSurface />;
}

function ExperimentalSurface() {
  const containerRef = useRef(null);
  const sceneApiRef = useRef(null);

  // Mode is fixed at mount; ?blind and ?v are read once.
  const modeRef = useRef(readModeFromUrl());
  const { blind, vExplicit } = modeRef.current;

  // Trial state (only meaningful in blind mode, but harmless in debug).
  const [trialId, setTrialId] = useState(0); // increments on "Next trial"
  const [variant, setVariant] = useState(() => vExplicit || pickRandom());
  const [phase, setPhase] = useState("playing"); // "playing" | "questions" | "revealed"
  const [answers, setAnswers] = useState({ q1: "", q2: "", q3: "" });
  const [savedTrials, setSavedTrials] = useState([]);
  // Facilitator-recorded moments of visible attention shift.
  // Populated only while phase === "playing"; captured via the `L` key
  // by the person operating the lab. Timestamps are seconds into the
  // arc (0..arcDuration). The viewer receives no cue about this
  // instrument — it exists to observe them, not vice versa.
  const [attentionEvents, setAttentionEvents] = useState([]);
  const [questionsAt, setQuestionsAt] = useState(null);

  // When "Next trial" is requested (blind only), pick a fresh variant.
  // Debug mode never re-randomizes.
  const advanceTrial = useCallback(() => {
    if (!blind) return;
    setVariant(pickRandom());
    setAnswers({ q1: "", q2: "", q3: "" });
    setAttentionEvents([]);
    setQuestionsAt(null);
    setPhase("playing");
    setTrialId((n) => n + 1);
  }, [blind]);

  const replaySame = useCallback(() => {
    setAnswers({ q1: "", q2: "", q3: "" });
    setAttentionEvents([]);
    setQuestionsAt(null);
    setPhase("playing");
    setTrialId((n) => n + 1); // reuses same `variant`; triggers scene rebuild
  }, []);

  // Mount / rebuild the scene whenever trialId changes.
  useEffect(() => {
    if (!containerRef.current) return;
    const api = createBeaconsScene({
      container: containerRef.current,
      initialParams: { ...BEACONS_DEFAULTS, variant },
    });
    sceneApiRef.current = api;
    return () => {
      sceneApiRef.current = null;
      api.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialId, variant]);

  // Poll scene state; when the arc reaches "done", flip to questions
  // and stamp the exact playback time at which questions became visible.
  useEffect(() => {
    if (phase !== "playing") return;
    let raf;
    const tick = () => {
      const api = sceneApiRef.current;
      if (api) {
        const s = api.getState();
        if (s.state === "done") {
          setQuestionsAt(Number(s.phaseDuration.toFixed(2)));
          setPhase("questions");
          return;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase, trialId]);

  // Facilitator key: press `L` during playback to record the moment the
  // viewer becomes more attentive. Broad phenomenon — lean-in, stillness,
  // uncrossed arms, narrowed eyes, held breath — any visible increase in
  // attention. No on-screen confirmation is shown; the viewer must not
  // learn that this instrument exists.
  useEffect(() => {
    if (phase !== "playing") return;
    const onKey = (e) => {
      if (e.key !== "l" && e.key !== "L") return;
      const target = e.target;
      // Ignore if the facilitator is somehow typing in a field.
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      const api = sceneApiRef.current;
      if (!api) return;
      const t = Number(api.getState().phaseT.toFixed(2));
      setAttentionEvents((prev) => [...prev, t]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, trialId]);

  const reveal = () => {
    setPhase("revealed");
    // Persist a lightweight trial record so live-session answers aren't lost.
    const record = {
      variant,
      attentionEvents,
      questionsAt,
      answers,
      at: new Date().toISOString(),
      mode: blind ? "blind" : "debug",
    };
    try {
      const key = "beacons_trials";
      const prev = JSON.parse(localStorage.getItem(key) || "[]");
      const next = [...prev, record];
      localStorage.setItem(key, JSON.stringify(next));
      setSavedTrials(next);
    } catch (_) {
      /* localStorage unavailable — silently continue */
    }
  };

  // Debug-mode badge (only shown when NOT blind AND an explicit variant was named).
  const showDebugBadge = !blind && vExplicit;

  return (
    <div
      data-testid="beacons-lab"
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        overflow: "hidden",
        color: "rgba(255,255,255,0.85)",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />

      {showDebugBadge && (
        <div
          data-testid="beacons-variant-badge"
          style={{
            position: "absolute",
            left: 16,
            bottom: 16,
            color: "rgba(255,255,255,0.35)",
            fontFamily: "monospace",
            fontSize: 11,
            letterSpacing: "0.08em",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          variant {VARIANT_LABELS[variant]}
        </div>
      )}

      {blind && phase === "playing" && (
        <div
          data-testid="beacons-blind-marker"
          style={{
            position: "absolute",
            left: 16,
            bottom: 16,
            color: "rgba(255,255,255,0.25)",
            fontFamily: "monospace",
            fontSize: 11,
            letterSpacing: "0.08em",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          trial #{trialId + 1}
        </div>
      )}

      {blind && phase === "questions" && (
        <QuestionsPanel
          answers={answers}
          onChange={setAnswers}
          onSubmit={reveal}
        />
      )}

      {blind && phase === "revealed" && (
        <RevealPanel
          variant={variant}
          answers={answers}
          attentionEvents={attentionEvents}
          questionsAt={questionsAt}
          savedCount={savedTrials.length}
          onReplay={replaySame}
          onNextTrial={advanceTrial}
        />
      )}
    </div>
  );
}

/* ---------- Presentational panels ---------- */

const panelBase = {
  position: "absolute",
  right: 0,
  top: 0,
  bottom: 0,
  width: "min(440px, 40vw)",
  background: "rgba(0,0,0,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  padding: "48px 40px",
  display: "flex",
  flexDirection: "column",
  gap: 24,
  overflowY: "auto",
};

const labelStyle = {
  fontSize: 12,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.42)",
  marginBottom: 8,
};

const questionStyle = {
  fontSize: 15,
  lineHeight: 1.55,
  color: "rgba(255,255,255,0.92)",
  marginBottom: 8,
  fontWeight: 400,
};

const textareaStyle = {
  width: "100%",
  minHeight: 68,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 6,
  color: "rgba(255,255,255,0.92)",
  padding: "10px 12px",
  fontSize: 14,
  fontFamily: "inherit",
  resize: "vertical",
  outline: "none",
};

const buttonPrimary = {
  background: "rgba(255,255,255,0.92)",
  color: "#000",
  border: "none",
  padding: "12px 20px",
  borderRadius: 6,
  fontSize: 13,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  cursor: "pointer",
  fontFamily: "inherit",
};

const buttonGhost = {
  background: "transparent",
  color: "rgba(255,255,255,0.72)",
  border: "1px solid rgba(255,255,255,0.24)",
  padding: "12px 20px",
  borderRadius: 6,
  fontSize: 13,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  cursor: "pointer",
  fontFamily: "inherit",
};

function QuestionsPanel({ answers, onChange, onSubmit }) {
  const set = (k) => (e) => onChange({ ...answers, [k]: e.target.value });
  // Three neutral prompts. Do not mention light, facets, geometry,
  // roughness, or beacons. Vocabulary comes from the observer, not us.
  return (
    <div data-testid="beacons-questions-panel" style={panelBase}>
      <div style={labelStyle}>after playback</div>

      <div>
        <div style={questionStyle}>1. What did you notice first?</div>
        <textarea
          data-testid="beacons-q1"
          style={textareaStyle}
          value={answers.q1}
          onChange={set("q1")}
          autoFocus
        />
      </div>

      <div>
        <div style={questionStyle}>
          2. Did the object seem to change, or did you begin seeing something
          that was already there?
        </div>
        <textarea
          data-testid="beacons-q2"
          style={textareaStyle}
          value={answers.q2}
          onChange={set("q2")}
        />
      </div>

      <div>
        <div style={questionStyle}>
          3. At any point did the surface feel designed to attract your
          attention?
        </div>
        <textarea
          data-testid="beacons-q3"
          style={textareaStyle}
          value={answers.q3}
          onChange={set("q3")}
        />
      </div>

      <button
        data-testid="beacons-reveal-btn"
        style={buttonPrimary}
        onClick={onSubmit}
      >
        Save and reveal
      </button>
      <div style={{ ...labelStyle, marginTop: -8 }}>
        answers save locally before the identity is shown
      </div>
    </div>
  );
}

function RevealPanel({ variant, answers, attentionEvents, questionsAt, savedCount, onReplay, onNextTrial }) {
  const eventsLine =
    attentionEvents && attentionEvents.length
      ? attentionEvents.map((t) => `${t.toFixed(1)}s`).join("  ·  ")
      : "none recorded";
  return (
    <div data-testid="beacons-reveal-panel" style={panelBase}>
      <div style={labelStyle}>this trial was</div>
      <div
        data-testid="beacons-revealed-variant"
        style={{
          fontSize: 28,
          fontWeight: 300,
          letterSpacing: "0.02em",
          color: "rgba(255,255,255,0.98)",
        }}
      >
        Variant {variant}
      </div>
      <div style={{ ...labelStyle, marginTop: -12 }}>
        {VARIANT_LABELS[variant].split(" · ")[1]}
      </div>

      <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "8px 0" }} />

      <div>
        <div style={labelStyle}>your answers</div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: "rgba(255,255,255,0.78)" }}>
          <p><strong style={{ color: "rgba(255,255,255,0.5)" }}>1.</strong> {answers.q1 || <em style={{ opacity: 0.4 }}>—</em>}</p>
          <p><strong style={{ color: "rgba(255,255,255,0.5)" }}>2.</strong> {answers.q2 || <em style={{ opacity: 0.4 }}>—</em>}</p>
          <p><strong style={{ color: "rgba(255,255,255,0.5)" }}>3.</strong> {answers.q3 || <em style={{ opacity: 0.4 }}>—</em>}</p>
        </div>
      </div>

      <div>
        <div style={labelStyle}>attention (facilitator)</div>
        <div
          data-testid="beacons-attention-events"
          style={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.62)" }}
        >
          {eventsLine}
          {questionsAt !== null && (
            <span style={{ opacity: 0.45 }}>  ·  questions at {questionsAt.toFixed(1)}s</span>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
        <button
          data-testid="beacons-replay-btn"
          style={buttonGhost}
          onClick={onReplay}
        >
          Replay same
        </button>
        <button
          data-testid="beacons-next-trial-btn"
          style={buttonPrimary}
          onClick={onNextTrial}
        >
          Next trial
        </button>
      </div>

      <div
        style={{
          marginTop: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={labelStyle}>
          {savedCount} trial{savedCount === 1 ? "" : "s"} saved this session
        </div>
        {savedCount > 0 && (
          <button
            data-testid="beacons-export-btn"
            style={{
              ...buttonGhost,
              alignSelf: "flex-start",
              padding: "8px 14px",
              fontSize: 11,
            }}
            onClick={downloadTrialsJson}
          >
            Export trials ({savedCount})
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------- Session data export ---------- */

// Reads localStorage['beacons_trials'] and triggers a browser download
// of a timestamped JSON file. Works in Safari without devtools.
function downloadTrialsJson() {
  let raw;
  try {
    raw = localStorage.getItem("beacons_trials") || "[]";
  } catch (_) {
    raw = "[]";
  }
  // Pretty-print for legibility when reviewing offline.
  let pretty = raw;
  try {
    pretty = JSON.stringify(JSON.parse(raw), null, 2);
  } catch (_) {
    /* keep raw */
  }
  const blob = new Blob([pretty], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const now = new Date();
  const stamp = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const a = document.createElement("a");
  a.href = url;
  a.download = `beacons_trials_${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a beat to consume the URL before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}


/* ---------- Debrief surface ----------
 *
 * `/lab/beacons?debrief=1[&shown=a|b|c]`
 *
 * Mobile-friendly, no scene, no facilitator instrument. Attendees who
 * saw the keynote can visit this URL on their phone and answer the same
 * three neutral questions they would have answered in the lab. If the
 * facilitator prepared the QR code with `&shown=B` (etc.), that value
 * is recorded on each response as `shownVariant` — attendees never see
 * or select it. Responses land in the same `beacons_trials` store as
 * lab trials, tagged with `mode: "debrief"`.
 */
function DebriefSurface() {
  const { shownVariant } = readModeFromUrl();
  const [answers, setAnswers] = useState({ q1: "", q2: "", q3: "" });
  const [submitted, setSubmitted] = useState(false);
  const [count, setCount] = useState(() => {
    try {
      const prev = JSON.parse(localStorage.getItem("beacons_trials") || "[]");
      return prev.filter((t) => t.mode === "debrief").length;
    } catch (_) {
      return 0;
    }
  });

  const set = (k) => (e) => setAnswers({ ...answers, [k]: e.target.value });

  const submit = () => {
    const record = {
      mode: "debrief",
      shownVariant,
      answers,
      at: new Date().toISOString(),
    };
    try {
      const prev = JSON.parse(localStorage.getItem("beacons_trials") || "[]");
      const next = [...prev, record];
      localStorage.setItem("beacons_trials", JSON.stringify(next));
      setCount(next.filter((t) => t.mode === "debrief").length);
    } catch (_) {
      /* localStorage unavailable — silently continue */
    }
    setSubmitted(true);
  };

  const reset = () => {
    setAnswers({ q1: "", q2: "", q3: "" });
    setSubmitted(false);
  };

  return (
    <div
      data-testid="beacons-debrief"
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        color: "rgba(255,255,255,0.88)",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: 560,
          margin: "0 auto",
          padding: "56px 24px 96px",
          display: "flex",
          flexDirection: "column",
          gap: 28,
        }}
      >
        {!submitted && (
          <>
            <div>
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.42)",
                  marginBottom: 10,
                }}
              >
                after the keynote
              </div>
              <div
                style={{
                  fontSize: 22,
                  lineHeight: 1.35,
                  fontWeight: 300,
                  color: "rgba(255,255,255,0.94)",
                }}
              >
                Thank you for watching. Three short questions, if you're willing.
              </div>
            </div>

            <DebriefQuestion
              testId="beacons-debrief-q1"
              number={1}
              prompt="What did you notice first?"
              value={answers.q1}
              onChange={set("q1")}
              autoFocus
            />
            <DebriefQuestion
              testId="beacons-debrief-q2"
              number={2}
              prompt="Did the object seem to change, or did you begin seeing something that was already there?"
              value={answers.q2}
              onChange={set("q2")}
            />
            <DebriefQuestion
              testId="beacons-debrief-q3"
              number={3}
              prompt="At any point did the surface feel designed to attract your attention?"
              value={answers.q3}
              onChange={set("q3")}
            />

            <button
              data-testid="beacons-debrief-submit"
              onClick={submit}
              style={{
                background: "rgba(255,255,255,0.94)",
                color: "#000",
                border: "none",
                padding: "16px 24px",
                borderRadius: 8,
                fontSize: 14,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                cursor: "pointer",
                fontFamily: "inherit",
                marginTop: 8,
              }}
            >
              Submit
            </button>

            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.32)",
              }}
            >
              answers save to this device
            </div>
          </>
        )}

        {submitted && (
          <div
            data-testid="beacons-debrief-thanks"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 20,
              padding: "80px 0",
            }}
          >
            <div
              style={{
                fontSize: 24,
                lineHeight: 1.35,
                fontWeight: 300,
                color: "rgba(255,255,255,0.94)",
              }}
            >
              Thank you.
            </div>
            <div
              style={{
                fontSize: 15,
                lineHeight: 1.55,
                color: "rgba(255,255,255,0.62)",
              }}
            >
              Your response has been recorded.
            </div>
            <button
              data-testid="beacons-debrief-reset"
              onClick={reset}
              style={{
                alignSelf: "flex-start",
                background: "transparent",
                color: "rgba(255,255,255,0.72)",
                border: "1px solid rgba(255,255,255,0.24)",
                padding: "12px 20px",
                borderRadius: 6,
                fontSize: 12,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                cursor: "pointer",
                fontFamily: "inherit",
                marginTop: 8,
              }}
            >
              Next person
            </button>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.32)",
                marginTop: "auto",
              }}
            >
              {count} debrief response{count === 1 ? "" : "s"} on this device
            </div>
            {count > 0 && (
              <button
                data-testid="beacons-debrief-export"
                onClick={downloadTrialsJson}
                style={{
                  alignSelf: "flex-start",
                  background: "transparent",
                  color: "rgba(255,255,255,0.55)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  padding: "10px 16px",
                  borderRadius: 6,
                  fontSize: 11,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Export all responses
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DebriefQuestion({ testId, number, prompt, value, onChange, autoFocus }) {
  return (
    <div>
      <div
        style={{
          fontSize: 15,
          lineHeight: 1.55,
          color: "rgba(255,255,255,0.92)",
          marginBottom: 10,
          fontWeight: 400,
        }}
      >
        <span style={{ color: "rgba(255,255,255,0.42)", marginRight: 8 }}>{number}.</span>
        {prompt}
      </div>
      <textarea
        data-testid={testId}
        value={value}
        onChange={onChange}
        autoFocus={autoFocus}
        style={{
          width: "100%",
          minHeight: 96,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.14)",
          borderRadius: 8,
          color: "rgba(255,255,255,0.94)",
          padding: "12px 14px",
          fontSize: 16, // 16px prevents iOS Safari from zooming on focus
          fontFamily: "inherit",
          lineHeight: 1.5,
          resize: "vertical",
          outline: "none",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}
